import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    FileText, ChevronLeft, Plus, Clock, CheckCircle,
    Edit2, Eye, Send, AlertCircle
} from 'lucide-react';
import { patientService } from '../services/patientService';
import './ClinicalNotesPage.css';

const ClinicalNotesPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [patient, setPatient] = useState(null);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submittingNote, setSubmittingNote] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [formData, setFormData] = useState({
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        note_type: 'consulta'
    });

    useEffect(() => {
        // Check authentication
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        fetchData();
    }, [id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [patientData, notesData] = await Promise.all([
                patientService.getById(id),
                patientService.getClinicalNotes(id, 100)
            ]);
            setPatient(patientData);
            setNotes(notesData.notes || notesData || []);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingNote) return;
        try {
            setSubmittingNote(true);
            await patientService.createClinicalNote({
                patient_id: parseInt(id),
                ...formData
            });
            setShowForm(false);
            setFormData({
                subjective: '',
                objective: '',
                assessment: '',
                plan: '',
                note_type: 'consulta'
            });
            fetchData();
        } catch (error) {
            console.error('Error creating note:', error);
        } finally {
            setSubmittingNote(false);
        }
    };

    const handleSign = async (noteId) => {
        try {
            await patientService.signClinicalNote(noteId);
            fetchData();
        } catch (error) {
            console.error('Error signing note:', error);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('es-MX', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Pagination logic
    const totalPages = Math.ceil(notes.length / itemsPerPage);
    const paginatedNotes = notes.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="clinical-notes-page">
            <div className="page-header">
                <button className="btn-back" onClick={() => navigate('/patients')}>
                    <ChevronLeft size={20} />
                    Volver
                </button>
                <div className="header-info">
                    <h1>
                        <FileText size={28} />
                        Notas Clínicas
                    </h1>
                    {patient && (
                        <p className="patient-name">
                            Paciente: {patient.first_name} {patient.last_name} {patient.maternal_last_name || ''}
                        </p>
                    )}
                </div>
                <button className="btn-primary" onClick={() => setShowForm(true)}>
                    <Plus size={18} />
                    Nueva Nota SOAP
                </button>
            </div>

            {loading ? (
                <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Cargando notas clínicas...</p>
                </div>
            ) : notes.length === 0 ? (
                <div className="empty-state">
                    <FileText size={64} />
                    <h3>No hay notas clínicas</h3>
                    <p>Crea la primera nota clínica para este paciente</p>
                    <button className="btn-primary" onClick={() => setShowForm(true)}>
                        <Plus size={18} />
                        Crear Nota
                    </button>
                </div>
            ) : (
                <>
                    <div className="notes-list">
                        {paginatedNotes.map(note => (
                            <div key={note.id} className={`note-card ${note.is_signed ? 'signed' : ''}`}>
                                <div className="note-header">
                                    <div className="note-meta">
                                        <span className="note-type">{note.note_type || 'Consulta'}</span>
                                        <span className="note-date">
                                            <Clock size={14} />
                                            {formatDate(note.created_at)}
                                        </span>
                                    </div>
                                    <div className="note-status">
                                        {note.is_signed ? (
                                            <span className="status-badge signed">
                                                <CheckCircle size={14} />
                                                Firmada
                                            </span>
                                        ) : (
                                            <span className="status-badge pending">
                                                <AlertCircle size={14} />
                                                Pendiente
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="note-content">
                                    <div className="soap-section">
                                        <h4>S - Subjetivo</h4>
                                        <p>{note.subjective || '-'}</p>
                                    </div>
                                    <div className="soap-section">
                                        <h4>O - Objetivo</h4>
                                        <p>{note.objective || '-'}</p>
                                    </div>
                                    <div className="soap-section">
                                        <h4>A - Análisis</h4>
                                        <p>{note.assessment || '-'}</p>
                                    </div>
                                    <div className="soap-section">
                                        <h4>P - Plan</h4>
                                        <p>{note.plan || '-'}</p>
                                    </div>
                                </div>

                                <div className="note-actions">
                                    <button className="btn-action view" title="Ver completo">
                                        <Eye size={16} />
                                    </button>
                                    {!note.is_signed && (
                                        <>
                                            <button className="btn-action edit" title="Editar">
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                className="btn-action sign"
                                                title="Firmar"
                                                onClick={() => handleSign(note.id)}
                                            >
                                                <Send size={16} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination-controls">
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                ← Anterior
                            </button>
                            <span className="pagination-info">
                                Página {currentPage} de {totalPages} ({notes.length} notas)
                            </span>
                            <button
                                className="pagination-btn"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                            >
                                Siguiente →
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* New Note Modal */}
            {showForm && (
                <div className="modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Nueva Nota Clínica SOAP</h2>
                            <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Tipo de Nota</label>
                                    <select
                                        value={formData.note_type}
                                        onChange={e => setFormData({ ...formData, note_type: e.target.value })}
                                    >
                                        <option value="consulta">Consulta</option>
                                        <option value="seguimiento">Seguimiento</option>
                                        <option value="urgencia">Urgencia</option>
                                        <option value="interconsulta">Interconsulta</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>S - Subjetivo</label>
                                    <textarea
                                        placeholder="Síntomas, historia del paciente..."
                                        value={formData.subjective}
                                        onChange={e => setFormData({ ...formData, subjective: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>O - Objetivo</label>
                                    <textarea
                                        placeholder="Signos vitales, examen físico..."
                                        value={formData.objective}
                                        onChange={e => setFormData({ ...formData, objective: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>A - Análisis</label>
                                    <textarea
                                        placeholder="Diagnóstico, interpretación..."
                                        value={formData.assessment}
                                        onChange={e => setFormData({ ...formData, assessment: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>P - Plan</label>
                                    <textarea
                                        placeholder="Tratamiento, seguimiento..."
                                        value={formData.plan}
                                        onChange={e => setFormData({ ...formData, plan: e.target.value })}
                                        rows={3}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit" disabled={submittingNote}>
                                    {submittingNote ? 'Guardando...' : 'Guardar Nota'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClinicalNotesPage;


